import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TID } from "../../lib/testIds";
import { SOLUTIONS, INDUSTRIES } from "../../data/pages";

const scrollToContactOr = (navigate, isHome) => {
  if (isHome) {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  }
};

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const solutionLinks = Object.values(SOLUTIONS).map((s) => ({
    label: s.eyebrow,
    to: `/solutions/${s.slug}`,
  }));
  const industryLinks = Object.values(INDUSTRIES).map((i) => ({
    label: i.eyebrow,
    to: `/industries/${i.slug}`,
  }));

  const forLinks = [
    { label: "SaaS Companies", href: null },
    { label: "Startups & Scaleups", href: null },
    { label: "Enterprises", href: null },
    { label: "Software Agencies", href: null },
  ];

  const companyLinks = [
    { label: "About", to: "/about" },
    { label: "Work", to: "/work" },
    { label: "Contact", action: () => scrollToContactOr(navigate, isHome) },
  ];

  return (
    <footer
      data-testid={TID.footer}
      className="relative bg-[#050505] border-t border-white/10"
    >
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <h3 className="font-headings text-2xl md:text-4xl font-black tracking-tight text-white leading-tight max-w-2xl">
            Have a product challenge? <span className="text-zinc-500">Start a conversation.</span>
          </h3>
          <button
            data-testid={TID.footerCta}
            onClick={() => scrollToContactOr(navigate, isHome)}
            className="group inline-flex items-center justify-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] text-black font-bold px-6 py-3.5 text-sm md:text-base transition-all self-start"
          >
            Start a Conversation
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4">
            <Link
              to="/"
              className="font-headings text-3xl md:text-4xl font-black tracking-tighter text-white flex items-center gap-3"
            >
              <span className="w-2 h-2 bg-[#FF5F1F] animate-accent-pulse" />
              Techovarya
            </Link>
            <p className="mt-6 text-sm text-zinc-400 leading-relaxed max-w-sm">
              AI-native product engineering for SaaS companies, technology businesses, and growing
              enterprises. Build intelligent products. Modernize complex platforms. Scale
              engineering delivery.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["USA", "UK", "Europe", "UAE", "Australia"].map((m) => (
                <span
                  key={m}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-white/10 text-zinc-500"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F] mb-5">
                Solutions
              </h4>
              <ul className="flex flex-col gap-3">
                {solutionLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F] mb-5">
                For
              </h4>
              <ul className="flex flex-col gap-3">
                {forLinks.map((l) => (
                  <li key={l.label}>
                    <span className="text-sm text-zinc-500 cursor-default">{l.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F] mb-5">
                Industries
              </h4>
              <ul className="flex flex-col gap-3">
                {industryLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F] mb-5">
                Company
              </h4>
              <ul className="flex flex-col gap-3">
                {companyLinks.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">
                        {l.label}
                      </Link>
                    ) : (
                      <button
                        onClick={l.action}
                        className="text-sm text-zinc-400 hover:text-white transition-colors text-left"
                      >
                        {l.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            © {new Date().getFullYear()} Techovarya. Built by engineers.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            Build smarter. Modernize faster. Scale with confidence.
          </p>
        </div>
      </div>
    </footer>
  );
};
